import { motion } from "framer-motion";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useProductData } from "@/zustand/productStore.js";
import { useCartData } from "@/zustand/cartStore.js";
import { useReducedProductsPrice } from "@/func/productCardHelpers.js";
import LazyLoad from "react-lazyload";
import ProductSelectorButton from "@/components/ProductSelectorButton";
import ProductPriceBlock from "@/components/ProductPackages/PriceBlock";
import { VariantPicker } from "@/components/ProductPackages/VariantPicker";
import DoubleTagIcon from "@/public/fallbacks/DoubleTagIcon.svg";
import { formatDisplayName } from "@/utils/helpers.jsx";
import glideImage from "@/func/glideImage.js";
import { isDiscountActive } from "@/utils/discounts";

const ProductPackageItem = forwardRef((props, ref) => {
  const productData = useProductData();
  const base = window.post_template;
  const { discountCode, variants, defaultSize, defaultColor, isSubscribe } = useCartData();
  const { product, handleChange, active, index, globalShippingLabel } = props;
  const [fadeIn, setFadeIn] = useState();
  const [variantImage, setVariantImage] = useState();
  const variantsRef = useRef();
  const isBestSeller = product.best_seller;
  const isBestValue = product.best_value;

  const productsContent = window.post_template?.checkout_v4_products;
  const outlineBorder = productsContent.checkout_v4_products_imageWrap;
  const showBadges = productsContent.checkout_v4_products_badgeShow;
  const colorVariantHeading = productsContent?.tr_checkout_v4_colorVariantHeading;
  const sizeVariantHeading = productsContent?.tr_checkout_v4_sizeVariantHeading;

  const showBulletPoints = base?.checkout_v4_summary.checkout_v4_summary_bullet_points ?? false;

  const productInfo = useReducedProductsPrice(product, productData?.currency_code, productData?.formatted_string);
  const { priceObject, promoPriceObject } = productInfo;

  const discountActive = isDiscountActive(productData, isSubscribe, discountCode);
  const productPriceObject = discountActive ? promoPriceObject : priceObject;
  const discountPercent = discountActive ? product.promo_discount_amount_percent : product.you_save_percentage;

  useEffect(() => {
    if (active) {
      setTimeout(() => {
        setFadeIn(true);
      }, 300);
    } else {
      setFadeIn(false);
    }
  }, [active]);

  const fallbackImages = window.post_template.checkout_v4_products.checkout_v4_product_images;
  const matchingImage =
    fallbackImages && fallbackImages.find((obj) => obj.product_amount === product.amount)?.product_image;

  useEffect(() => {
    if (productData?.variants_picker_type == "separate" && variants) {
      setVariantImage(
        productData?.color_variants?.find((item) => {
          return item.value == variants[0]?.color;
        })?.variant_image
      );
    } else {
      return;
    }
  }, [variants]);

  const defaultImage = matchingImage ? matchingImage : product.image.url;
  const image = glideImage(variantImage ? variantImage : defaultImage, { h: 170 });

  return (
    <div
      className={`productPackages__containerWrapper ${
        active ? "productPackage__container--visible" : "productPackages__container--unchecked"
      }`}
    >
      <div
        data-testid="product-container"
        onClick={() => {
          handleChange(index);
        }}
        id={`productCard-${product.amount}`}
        className={`productPackages__container ${globalShippingLabel && "freeShipping"}`}
      >
        <div className="productPacItem__firstBlock">
          <div style={{ display: "flex", alignItems: "center" }}>
            {showBulletPoints !== undefined && showBulletPoints !== null && !showBulletPoints && (
              <ProductSelectorButton />
            )}
            <LazyLoad offset={100}>
              <motion.div
                key={image}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 1,
                  default: { duration: 0.5 },
                }}
              >
                <img
                  className={`productPacItem__image ${globalShippingLabel && "freeShipping"} ${outlineBorder ? "productPacItem__image--border" : ""} mon-img-170-h`}
                  src={image}
                  alt={product.image.alt}
                />
              </motion.div>
            </LazyLoad>
          </div>
          <div className={`productPacItem__label__container ${globalShippingLabel && "freeShipping"}`}>
            <span data-testid="product-amount" className="productPacItem__label__amount">
              {product.amount}x
            </span>
            <span className="productPacItem__label">{formatDisplayName(product.display_name)}</span>
            <div className="productPacItem__discount__box desktopHide">
              <img src={DoubleTagIcon} />
              {discountPercent} %
            </div>
          </div>
        </div>
        <div className="productPacItem__divider mobileHide"></div>
        <div className="productPacItem__discount__container mobileHide">
          <div className="productPacItem__discount__box" style={!showBadges ? { opacity: "0" } : {}}>
            <img src={DoubleTagIcon} />
            {discountPercent}%
          </div>
        </div>

        <div className="productPacItem__divider"></div>

        <ProductPriceBlock
          priceObject={productPriceObject}
          oldPrice={productInfo.oldPrice}
          isBestValue={isBestValue}
          isBestSeller={isBestSeller}
          product={product}
          bundledPrices={productData?.show_bundle_prices}
        />
      </div>
      {active && (
        <div
          className={`variants-container ${fadeIn ? "variants-container--show" : "variants-container--hide"}`}
          style={fadeIn ? { maxHeight: `${variantsRef?.current?.clientHeight}px` } : { maxHeight: "0px" }}
        >
          <div ref={variantsRef}>
            {productData?.enable_color_variants && productData?.variants_picker_type == "each" && (
              <VariantPicker
                defaultValue={defaultColor}
                type="color"
                heading={colorVariantHeading}
                id={product?.amount}
                data={productData?.product_color_variants}
                numberOfItems={product?.amount}
              />
            )}
            {productData?.enable_size_variants && productData?.variants_picker_type == "each" && (
              <VariantPicker
                defaultValue={defaultSize}
                type="size"
                heading={sizeVariantHeading}
                id={product?.amount}
                data={productData?.product_size_variants}
                numberOfItems={product?.amount}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export { ProductPackageItem };
